import { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

interface ReceiptUploadProps {
  onUploadSuccess: () => void;
}

export function ReceiptUpload({ onUploadSuccess }: ReceiptUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetUpload = () => {
    setIsUploading(false);
    setError(null);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setError('Please select a file');
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload a PDF, PNG, or JPEG file');
      event.target.value = ''; // Reset file input
      return;
    }

    setIsUploading(true);
    setError(null);

    if (!supabase) {
      setError('Supabase client not initialized');
      setIsUploading(false);
      return;
    }

    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Upload file to Supabase Storage
      const filePath = `${user.id}/${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('receipts')
        .upload(filePath, file);

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      // Get the public URL for the uploaded file
      const { data: urlData } = supabase
        .storage
        .from('receipts')
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) throw new Error('Failed to get public URL');

      // Add the expense record
      const { error: supabaseError } = await supabase.from('expenses').insert({
        user_id: user.id,
        amount: 0.01, // Temporary amount to satisfy constraint
        charge_date: new Date().toISOString(),
        merchant: 'Processing...', // This will be updated by the AI processing
        category: 'Uncategorized', // This will be updated by the AI processing
        receipt_url: urlData.publicUrl,
      });

      if (supabaseError) throw new Error(`Database error: ${supabaseError.message}`);

      // Reset the file input
      event.target.value = '';
      resetUpload();
      onUploadSuccess();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload receipt';
      setError(errorMessage);
      console.error('Upload error:', errorMessage);
      event.target.value = ''; // Reset file input
    } finally {
      // Only set isUploading to false if there was an error
      // Success case is handled in try block
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-center w-full">
        <label
          htmlFor="receipt-upload"
          className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer 
            ${isUploading ? 'bg-gray-50 border-gray-300' : 'border-indigo-300 hover:bg-indigo-50'} 
            transition-colors duration-200`}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            {isUploading ? (
              <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
            ) : (
              <>
                <Upload className="h-12 w-12 text-indigo-500 mb-3" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF, PNG, or JPEG</p>
              </>
            )}
          </div>
          <input
            id="receipt-upload"
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </label>
      </div>
      {error && (
        <div className="mt-4 p-4 bg-red-50 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
}